<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { posStore } from "../../../../../stores/posStore";
import { formatNumber, isEmpty } from "../../../../../utils/CommonFunctions";
import IconComponent from "../../../../../components/ui/common/IconComponent.vue";
import { api } from "../../../../../api/Api";
import Toast from "../../../../../services/Toast";
import {
  CashierDuty,
  DeliveryTracking,
  ItemCategory,
  ItemGroup,
  PaymentType,
  PosStation,
  RecordStatus,
  RestaurantTables,
  SalesRep,
} from "../../../../../types/models";
import DropDownButton from "../../../../../components/ui/common/DropDownButton.vue";
import { User } from "../../../../../types/auth";
import MessageBox from "../../../../../services/MessageBox";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";
import Buttons from "../../../../../components/ui/form/Buttons.vue";
import DataTablePagination from "../../../../../components/table/DataTablePagination.vue";

defineProps<{
  payment_types: PaymentType[];
  item_categories: ItemCategory[];
  item_groups: ItemGroup[];
  delivery_staffs: User[];
  posStation: PosStation | undefined;
  cashierDuty: CashierDuty | null;
  salesReps: SalesRep[];
  restaurantTables: RestaurantTables[];
}>();

const emit = defineEmits<{
  (e: "new"): void;
  (e: "back"): void;
  (e: "order", table: RestaurantTables): void;
  (e: "order-created", tables: RestaurantTables[]): void;
  (e: "show-bill", table: RestaurantTables): void;
  (e: "make-bill", table: RestaurantTables): void;
}>();

const pos = posStore();
const activeTab = ref("order");
//const customerSearch = ref("");
const showCustomerList = ref(false);
const pendingDeliveries = ref<DeliveryTracking[]>([]);
const endPoint = "revenue/sales/pos/";

defineExpose({
  triggerCheckout() {
    if (
      !pos.showCheckoutModal &&
      (pos.selectedPosInvoice?.invoice.items?.length ?? 0) > 0
    ) {
      pos.showCheckoutModal = true;
    }
  },
});

const tabs = ref([
  { id: "order", label: "Orders", icon: "ShoppingCart" },
  { id: "deliveries", label: "Deliveries", icon: "Truck", badge: "" },
  { id: "notifications", label: "Notifications", icon: "Bell", badge: "" },
]);

// Sample data for pending deliveries

// Sample data for notifications
const notifications = ref<any[]>([]);

const handleStateChanged = () => {
  delivery_citys_id.value = "";
  getPendingDeliveries();
};
const handleCityChanged = () => {
  delviery_states_id.value = "";
  getPendingDeliveries();
};

const deliveryPageChange = (page: number) => {
  deliveryCurrentPage.value = page;
  getPendingDeliveries();
};

const getPendingDeliveries = async () => {
  const response = await api.post(endPoint + "deliverys", {
    search_query: deliverySearchQuery.value,
    states_id: delviery_states_id.value,
    citys_id: delivery_citys_id.value,
    per_page: 10,
    page: deliveryCurrentPage.value,
    details: true,
  });
  if (response.success == true) {
    setPendingDeliveries(response);
  } else {
    Toast.show("Could not retrieve Pending Delivery list", "warning");
  }
};

const deliveryCurrentPage = ref(1);
const deliveryTotalPages = ref(1);
const deliverySearchQuery = ref("");
const delivery_users = ref<User[]>([]);
const record_status = ref<RecordStatus[]>([]);
const delivery_citys_id = ref("");
const delviery_states_id = ref("");
const states = ref<{ id: any; name: any }[]>([]);
const citys = ref<{ id: any; name: any }[]>([]);

const setPendingDeliveries = (response: any) => {
  pendingDeliveries.value = response.data.data;
  deliveryTotalPages.value = response.data.total;
  if (response.lists && response.lists.status) {
    record_status.value = response.lists.status;
  }
  if (response.lists && response.lists.users)
    delivery_users.value = response.lists.users;
  let tab = tabs.value.find((tab) => tab.id == "deliveries");
  if (tab) {
    tab.badge = (pendingDeliveries.value.length ?? "").toString();
  }
  if (!delviery_states_id.value && !delivery_citys_id.value) {
    const uniqueSet = new Set();
    states.value = pendingDeliveries.value
      .filter((item: any) => {
        const uniqueIdName = `${item.states_id}-${item.state}`;
        if (!uniqueSet.has(uniqueIdName) && !isEmpty(item.state)) {
          uniqueSet.add(uniqueIdName);
          return true;
        }
        return false;
      })
      .map((item: any) => ({
        id: item.states_id,
        name: item.state,
      }));
    citys.value = pendingDeliveries.value
      .filter((item: any) => {
        const uniqueIdName = `${item.citys_id}-${item.city}`;
        if (!uniqueSet.has(uniqueIdName) && !isEmpty(item.city)) {
          uniqueSet.add(uniqueIdName);
          return true;
        }
        return false;
      })
      .map((item: any) => ({
        id: item.citys_id,
        name: item.city,
      }));
  }
};

const assignDeliveryStaff = async (delivery: DeliveryTracking, user: User) => {
  const params = {
    id: delivery.uid,
    users_id: user.uid,
    page: deliveryCurrentPage.value,
    per_page: 25,
    search_query: deliverySearchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "assign-user",
    params
  );
  if (response.success == true) {
    setPendingDeliveries(response);
  }
};

const updateDeliveryStatus = async (
  delivery: DeliveryTracking,
  status: RecordStatus
) => {
  let attachment = null;
  let remarks = null;
  const confirmed = await MessageBox.confirm({
    title: "Update Delivery",
    message: status.require_attachment
      ? "Please upload the Delivery Confimration"
      : "Are you sure you want to update the delivery status?",
    confirmText: "Update as " + status.name,
    requireRemarks: status.require_remarks,
    requireAttachments: status.require_attachment,
    showRemarks: true,
    cancelText: "Cancel",
    type: "info",
  });
  if (confirmed) {
    attachment = confirmed.attachment;
    remarks = confirmed.remarks;
    if (status.require_attachment && !attachment) return;
    if (status.require_remarks && !remarks) return;
  } else {
    return;
  }

  const params = {
    id: delivery.uid,
    delivery_status_id: status.id,
    attachment: attachment,
    remarks: remarks,
    page: deliveryCurrentPage.value,
    per_page: 25,
    search_query: deliverySearchQuery.value,
    filters: null,
  };
  const response = await api.post(
    (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + "update-delivery",
    params
  );
  if (response.success == true) {
    setPendingDeliveries(response);
  }
};

const getPendingDeliveryStatus = (delivery: DeliveryTracking) => {
  let status = record_status.value.find(
    (e) => e.linked_index !== undefined && e.linked_index >= 1
  );
  let res = record_status.value.filter(
    (e) => e.id > (delivery.status_id ?? 0) && e.name !== "Delivery Assigned"
  );
  return status && !isEmpty(status)
    ? res.filter((e) => e.linked_index != status.linked_index)
    : res;
};

const searchCustomerInput = ref<HTMLInputElement | null>(null);
const focusCustomerSearch = () => {
  searchCustomerInput.value?.focus();
};

onMounted(() => {
  window.addEventListener("keydown", handleKeyPress, true);
  //  getPendingDeliveries();
  setInterval(getPendingDeliveries, 300000); // 300000 ms = 5 minutes
});

const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === "F3") {
    event.preventDefault();
    showCustomerList.value = true;
  } else if (event.key === "F4") {
    event.preventDefault();
    focusCustomerSearch();
  } else if (event.key === "F12") {
    event.preventDefault();
    if ((pos.selectedPosInvoice?.invoice.items?.length ?? 0) > 0) {
      pos.showCheckoutModal = true;
    }
  }
};

const viewOrder = (table: RestaurantTables) => {
  emit("show-bill", table);
};

const makeBill = (table: RestaurantTables) => {
  emit("make-bill", table);
};

watch(
  () => pos.showCheckoutModal,
  () => {
    if (pos.showCheckoutModal) {
      window.removeEventListener("keydown", handleKeyPress);
    } else {
      window.window.addEventListener("keydown", handleKeyPress, true);
    }
  }
);
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700">
      <div class="flex">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="flex-1 px-4 py-3 text-sm font-medium text-center relative"
          :class="[
            activeTab === tab.id
              ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
          ]"
        >
          <div class="flex flex-col items-center">
            <IconComponent :name="tab.icon" class="block text-lg mb-1" />
            {{ tab.label }}
          </div>
          <span
            v-if="tab.badge"
            class="absolute top-2 right-2 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-medium text-white bg-red-500 rounded-full"
          >
            {{ tab.badge }}
          </span>
        </button>
      </div>
    </div>

    <!-- Content Area -->
    <div class="flex-1 flex flex-col min-h-0">
      <!-- Scrollable Content -->
      <div class="flex-1 overflow-y-auto">
        <!-- Order Tab -->
        <div v-if="activeTab === 'order'" class="p-4">
          <div
            v-if="restaurantTables.length === 0"
            class="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400"
          >
            <span class="text-4xl mb-2">🛒</span>
            <p>There are no orders</p>
          </div>
          <div v-else class="space-y-2">
            <template
              v-for="item in restaurantTables.filter((e) => !isEmpty(e.orders))"
              :key="item.id"
            >
              <div
                class="grid grid-cols-4 gap-2 flex-shrink-0 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg relative"
                tabindex="0"
              >
                <div class="col-span-2">
                  <h4
                    class="font-medium text-gray-900 dark:text-white truncate"
                  >
                    {{ item.table_number }}
                  </h4>
                </div>
                <div class="text-right min-w-[80px]">
                  {{ formatNumber(item.orders?.total_amount) }}
                </div>
                <div
                  class="text-right min-w-[80px] px-5 flex gap-1 justify-center"
                >
                  <Buttons
                    size="sm"
                    type="info"
                    icon="Eye"
                    @click="viewOrder(item)"
                    caption=""
                  />
                  <Buttons
                    size="sm"
                    type="success"
                    icon="Banknote"
                    @click="makeBill(item)"
                    caption=""
                  />
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Pending Deliveries Tab -->
        <div v-else-if="activeTab === 'deliveries'" class="p-4">
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <SearchSelect
                label="State"
                @select="handleStateChanged"
                v-model="delviery_states_id"
                :options="states"
                :remote="false"
              />
              <SearchSelect
                label="City"
                @select="handleCityChanged"
                v-model="delivery_citys_id"
                :options="citys"
                :remote="false"
              />
            </div>
            <div
              v-for="delivery in pendingDeliveries"
              :key="delivery.id"
              class="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div class="flex justify-between items-start mb-2">
                <div>
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ delivery.related_document_no }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ delivery.customer_name }}
                  </p>
                </div>
                <div class="text-right">
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :style="{ backgroundColor: delivery.status_color }"
                  >
                    {{ delivery.status }}
                  </span>
                  <br />
                  <a :href="'tel:' + delivery.customer_mobile">{{
                    delivery.customer_mobile
                  }}</a>
                </div>
              </div>
              <div
                class="flex justify-between text-sm items-center mt-2 overflow-hidden text-ellipsis whitespace-nowrap"
              >
                {{ delivery.full_address }}
              </div>
              <div class="flex justify-between items-center mt-2">
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  <div class="flex items-center gap-2">
                    <div class="flex items-center gap-2">
                      <DropDownButton :hideArrow="true">
                        <button
                          @click="updateDeliveryStatus(delivery, status)"
                          v-for="status in getPendingDeliveryStatus(delivery)"
                          class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <IconComponent :name="status.icon_css" />
                          {{ status.name }}
                        </button>
                      </DropDownButton>
                      <template v-if="!isEmpty(delivery.assigned_to_user)">
                        <IconComponent name="UsersRound" />
                        <span>{{ delivery.assigned_to_user }}</span>
                      </template>
                    </div>
                    <DropDownButton
                      :hideArrow="true"
                      icon="UsersRound"
                      v-if="isEmpty(delivery.assigned_to_user)"
                    >
                      <button
                        @click="assignDeliveryStaff(delivery, user)"
                        v-for="user in delivery_users"
                        class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {{ user.name }}
                      </button>
                    </DropDownButton>
                  </div>
                </span>
                <span class="font-medium text-gray-900 dark:text-white">
                  {{ delivery.delivery_date }}
                </span>
              </div>
            </div>
          </div>
          <div>
            <DataTablePagination
              :current-page="deliveryCurrentPage"
              :total-pages="deliveryTotalPages"
              @pageChange="deliveryPageChange"
            />
          </div>
        </div>

        <!-- Notifications Tab -->
        <div v-else-if="activeTab === 'notifications'" class="p-4">
          <div class="space-y-4">
            <div
              v-for="notification in notifications"
              :key="notification.id"
              class="bg-white dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div class="flex gap-4">
                <div
                  class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-xl"
                >
                  {{ notification.icon }}
                </div>
                <div class="flex-1">
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ notification.title }}
                  </h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ notification.message }}
                  </p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {{ notification.time }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
